// @flow
import ResourceCollection from "../base/ResourceCollection";
import ErrorCollection from "../error/ErrorCollection";

import createAttribute from "./_createAttribute";

import type { AttributeType, ModelOptions } from "../types";

/**
 * Collection of choice attribute options
 */
class CompositeAttributeChildCollection extends ResourceCollection<AttributeType> {
  /**
   */
  static create(
    data: Object,
    contributions: Object,
    modelOptions?: ModelOptions,
  ): CompositeAttributeChildCollection {
    if (
      data &&
      contributions &&
      Array.isArray(contributions.children) &&
      Array.isArray(data.children)
    ) {
      // a composite with multiple data elements and one contributions element
      // multiple 'composites' are returned
      const isMultipleTableRows = Array.isArray(data.children[0]);
      if (isMultipleTableRows) {
        return CompositeAttributeChildCollection.createMultiData(
          data,
          contributions,
          modelOptions,
        );
      }

      return CompositeAttributeChildCollection.createStandard(
        data,
        contributions,
        modelOptions,
      );
    }

    return new CompositeAttributeChildCollection();
  }

  /**
   */
  static createStandard(
    data: Object,
    contributions: Object,
    modelOptions?: ModelOptions,
  ): CompositeAttributeChildCollection {
    const compositeChildCollection = new CompositeAttributeChildCollection();

    compositeChildCollection.collection = contributions.children
      .map((contribution) => {
        const [key] = Object.keys(contribution);

        const attributeData = Array.isArray(data.children)
          ? data.children.find((attr) => attr.key === key)
          : {};

        const attributeContribution = contribution[key];

        if (
          contributions.type.includes("range") &&
          !attributeContribution.type
        ) {
          attributeContribution.type = contributions.type.replace("range", "");
        }

        const attribute = createAttribute(
          key,
          {
            ...attributeData,
            dynamicschema: attributeData?.dynamicschema,
          },
          {
            ...attributeContribution,
            readonly: attributeContribution.readonly || contributions.readonly,
          },
          modelOptions,
        );

        if (attribute) {
          attribute.parentKey = data.key;
        }

        return attribute;
      })
      .filter((attribute) => attribute !== null);

    return compositeChildCollection;
  }

  /*
   * Creates a composite for each child data element using the same contributions
   */
  /**
   */
  static createMultiData(
    data: Object,
    contributions: Object,
    modelOptions?: ModelOptions,
  ): CompositeAttributeChildCollection {
    const compositeChildCollection = new CompositeAttributeChildCollection();

    compositeChildCollection.collection = data.children.map((childData) =>
      CompositeAttributeChildCollection.create(
        {
          ...data,
          children: childData,
        },
        contributions,
        modelOptions,
      ),
    );

    return compositeChildCollection;
  }

  /**
   * Get input value of contributions
   */
  getInputValue(): string {
    return this.collection.map((child) => child.inputvalue).join(",");
  }

  /**
   * Validate input
   */
  validate(): boolean {
    return this.collection.every((child) => child.isValid);
  }

  /**
   * Retrieve error messages of this attribute
   */
  get errorCollection(): ErrorCollection {
    return new ErrorCollection("compositechildren");
  }

  /**
   */
  hasServerErrors(): boolean {
    return (
      this.errorCollection.serverErrors.length > 0 ||
      this.collection.some((child) => child.hasServerErrors())
    );
  }

  /**
   */
  hasErrors(): boolean {
    return (
      this.errorCollection.hasItems ||
      this.collection.some((child) => child.hasErrors())
    );
  }

  /**
   * Reset attribute to empty string
   */
  reset() {
    this.collection.forEach((child) => {
      child.reset();
    });
  }

  /**
   */
  get initvalue(): any {
    if (this.first instanceof CompositeAttributeChildCollection) {
      // $FlowFixMe[missing-type-arg]
      return this.collection.map<Array<Object>>(
        (collection) => collection.initvalue,
      );
    }

    const children: { [string]: any } = {};
    this.collection
      .filter((child) => child.initvalue !== null)
      .forEach((child) => {
        children[child.key] = child.initvalue;
      });

    if (Object.keys(children).length === 0) {
      return null;
    }

    return children;
  }

  /**
   */
  get value(): any {
    if (this.first instanceof CompositeAttributeChildCollection) {
      return this.collection.map((collection) => collection.value);
    }

    let children: { [string]: any } = {};

    this.collection.forEach((attribute) => {
      if (attribute.formdata !== null && !attribute.readonly) {
        children = {
          ...children,
          ...attribute.formdata,
        };
      }
    });

    if (Object.keys(children).length === 0) {
      return null;
    }

    return children;
  }

  /**
   */
  get readonlyvalue(): string {
    if (this.first instanceof CompositeAttributeChildCollection) {
      return this.collection
        .map((collection) => collection.readonlyvalue)
        .join(" · ");
    }

    return this.collection
      .filter((child) => child.value !== null)
      .map((child) => child.readonlyvalue)
      .join(", ");
  }

  /**
   */
  updateValidations(prefix: string, errors: Object) {
    this.collection.forEach((child) => {
      const attributeErrors = errors.filter(
        (error) => error.anchor?.elementid === `${prefix}${child.key}`,
      );

      child.updateValidations(attributeErrors);
    });
  }

  /**
   */
  hasValue(): boolean {
    return this.collection.some((child) => child.hasValue());
  }
}

export default CompositeAttributeChildCollection;
